import React from 'react';

export interface IconButtonProps {
  /** Lucide icon or ReactNode */
  icon: React.ReactNode;
  /** Container size */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Visual style */
  variant?: 'ghost' | 'tonal' | 'plain';
  /** Container shape */
  shape?: 'pill' | 'rounded';
  /** Active state (e.g. for navigation) */
  active?: boolean;
  /** Click handler */
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** Disabled state */
  disabled?: boolean;
  /** Accessible label */
  'aria-label'?: string;
  /** Tooltip text */
  title?: string;
  /** Additional CSS classes */
  className?: string;
}

const sizeMap: Record<string, { container: string; icon: string }> = {
  xs: { container: 'w-7 h-7', icon: 'w-3 h-3' },
  sm: { container: 'w-8 h-8', icon: 'w-3.5 h-3.5' },
  md: { container: 'w-[34px] h-[34px]', icon: 'w-[14px] h-[14px]' },
  lg: { container: 'w-[42px] h-[42px]', icon: 'w-5 h-5' },
  xl: { container: 'w-12 h-12', icon: 'w-[22px] h-[22px]' },
};

/**
 * Canonical icon button – consolidates 8+ icon container patterns.
 *
 * Variants:
 * - `ghost`: Border + transparent bg (most common, used in topbar, activity actions)
 * - `tonal`: Colored bg (for nav active state, feature card icons)
 * - `plain`: No border, no bg (minimal)
 *
 * Shapes:
 * - `pill`: Fully round (default, for action buttons)
 * - `rounded`: Rounded-sm (for nav icons, sidebar)
 */
export const IconButton: React.FC<IconButtonProps> = ({
  icon,
  size = 'md',
  variant = 'ghost',
  shape = 'pill',
  active = false,
  onClick,
  disabled = false,
  title,
  className = '',
  ...rest
}) => {
  const { container, icon: iconSize } = sizeMap[size] || sizeMap.md;

  const shapeClass = shape === 'pill' ? 'rounded-pill' : 'rounded-sm';

  const variantStyles: Record<string, string> = {
    ghost: [
      'border border-default bg-transparent text-txt-tertiary',
      'hover:border-strong hover:text-txt',
    ].join(' '),
    tonal: active
      ? 'bg-accent-soft text-accent border-0'
      : 'bg-bg-surface text-txt-tertiary border-0 hover:text-txt-secondary',
    plain: 'border-0 bg-transparent text-txt-tertiary hover:text-txt',
  };

  const activeOverride = active && variant === 'ghost'
    ? 'bg-accent-soft text-accent border-accent'
    : '';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={rest['aria-label'] || title}
      className={`
        ${container}
        ${shapeClass}
        grid place-items-center
        cursor-pointer
        transition-all duration-150 ease-spring-smooth
        disabled:opacity-50 disabled:cursor-not-allowed
        ${variantStyles[variant] || variantStyles.ghost}
        ${activeOverride}
        ${className}
      `.trim().replace(/\s+/g, ' ')}
    >
      {React.isValidElement(icon)
        ? React.cloneElement(icon as React.ReactElement<any>, { className: iconSize })
        : icon
      }
    </button>
  );
};

export default IconButton;
